import { useState } from "react";
import { C, Btn, Input, Select, Textarea, StatCard, Tabs, Modal, fmt, useIsMobile } from "./ui";
import { toast } from "../services/toast";

const TIPOS_EVENTO = ["Entore", "Tacto", "Parto", "Celo", "Destete", "Otro"];
const ICON_EV = { Entore: "🐂", Tacto: "🔬", Parto: "🐮", Celo: "🔥", Destete: "🍼", Otro: "📋" };
const hoyISO = () => new Date().toISOString().slice(0, 10);
const anioActual = () => new Date().getFullYear().toString();

export function ReproduccionPanel({ eventos = [], pesadas = [], animales = [], onAddEvento }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("repro");
  const tabs = [{ id: "repro", label: "Reproducción" }, { id: "peso", label: "Ganancia de peso" }];

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "16px 14px 90px" : "20px 24px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>Reproducción y peso</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Índices reproductivos y evolución de peso del rodeo. Guardado en base de datos.</div>
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
        {tab === "repro" && <TabRepro eventos={eventos} animales={animales} onAddEvento={onAddEvento} />}
        {tab === "peso" && <TabPeso pesadas={pesadas} />}
      </div>
    </div>
  );
}

// ─── TAB REPRODUCCIÓN ─────────────────────────────────────
function TabRepro({ eventos, animales, onAddEvento }) {
  const [show, setShow] = useState(false);
  const anio = anioActual();
  const delAnio = eventos.filter(e => (e.fecha || "").slice(0, 4) === anio);
  const cont = (tipo) => delAnio.filter(e => e.tipo === tipo).length;
  const entores = cont("Entore"), tactos = cont("Tacto"), partos = cont("Parto"), destetes = cont("Destete");
  const idxParicion = entores > 0 ? Math.round((partos / entores) * 100) : null;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginBottom: 16 }}>
        <StatCard val={entores} label={`entores ${anio}`} />
        <StatCard val={tactos} label="tactos" />
        <StatCard val={partos} label="pariciones" color={C.ok} />
        <StatCard val={destetes} label="destetes" />
        <StatCard val={idxParicion != null ? `${idxParicion}%` : "—"} label="índice parición" color={C.pasto} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Btn variant="primary" onClick={() => setShow(true)} disabled={animales.length === 0}>+ Registrar evento</Btn>
      </div>

      {eventos.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🐮</div>
          <div style={{ fontSize: 13 }}>Sin eventos reproductivos. Registrá entores, tactos, pariciones y destetes.</div>
        </div>
      ) : eventos.slice(0, 60).map(e => (
        <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 11, background: C.white, borderRadius: 10, padding: "10px 14px", marginBottom: 6, border: `1px solid ${C.arenaDark}` }}>
          <span style={{ fontSize: 19 }}>{ICON_EV[e.tipo] || "📋"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: C.tierra }}>{e.tipo} · #{e.caravana || "—"}
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 400, marginLeft: 6 }}>{e.categoria || ""}</span></div>
            {e.obs && <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{e.obs}</div>}
          </div>
          <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{fmt(e.fecha)}</span>
        </div>
      ))}

      {show && <ModalEvento animales={animales} onClose={() => setShow(false)} onAddEvento={onAddEvento} />}
    </div>
  );
}

function ModalEvento({ animales, onClose, onAddEvento }) {
  const [animalId, setAnimalId] = useState(animales[0]?.id || "");
  const [tipo, setTipo] = useState(TIPOS_EVENTO[0]);
  const [fecha, setFecha] = useState(hoyISO());
  const [obs, setObs] = useState("");
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    if (!animalId || guardando) return;
    setGuardando(true);
    try {
      await onAddEvento(animalId, { tipo, fecha, obs });
      toast.success("Evento reproductivo registrado.");
      onClose();
    } catch (e) {
      toast.error("No se pudo registrar: " + (e.message || "error"));
    } finally { setGuardando(false); }
  };

  return (
    <Modal title="Registrar evento reproductivo" onClose={onClose} width={460}>
      <Select label="Animal" value={animalId} onChange={setAnimalId}
        options={animales.map(a => ({ v: a.id, l: `#${a.caravana || "—"} · ${a.categoria || ""}` }))} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Select label="Tipo" value={tipo} onChange={setTipo} options={TIPOS_EVENTO} />
        <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
      </div>
      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2} />
      <Btn variant="primary" onClick={guardar} disabled={!animalId || guardando} style={{ width: "100%" }}>
        {guardando ? "Guardando…" : "Registrar"}
      </Btn>
    </Modal>
  );
}

// ─── TAB GANANCIA DE PESO ─────────────────────────────────
function TabPeso({ pesadas }) {
  // Agrupar pesadas por animal
  const porAnimal = {};
  pesadas.forEach(p => {
    if (!p.kg || !p.fecha) return;
    (porAnimal[p.animal_id] = porAnimal[p.animal_id] || { categoria: p.categoria, ps: [] }).ps.push(p);
  });

  const filas = []; // por animal: {categoria, gdp, ultimoPeso}
  Object.values(porAnimal).forEach(a => {
    const ps = a.ps.sort((x, y) => x.fecha.localeCompare(y.fecha));
    const ultimoPeso = ps[ps.length - 1].kg;
    if (ps.length >= 2) {
      const dias = (new Date(ps[ps.length - 1].fecha) - new Date(ps[0].fecha)) / 86400000;
      if (dias > 0) filas.push({ categoria: a.categoria || "Sin categoría", gdp: (ps[ps.length - 1].kg - ps[0].kg) / dias, ultimoPeso });
    } else {
      filas.push({ categoria: a.categoria || "Sin categoría", gdp: null, ultimoPeso });
    }
  });

  const conGdp = filas.filter(f => f.gdp != null);
  const gdpProm = conGdp.length ? conGdp.reduce((s, f) => s + f.gdp, 0) / conGdp.length : null;
  const pesoProm = filas.length ? filas.reduce((s, f) => s + f.ultimoPeso, 0) / filas.length : null;

  // Por categoría
  const cats = {};
  filas.forEach(f => {
    const c = (cats[f.categoria] = cats[f.categoria] || { n: 0, peso: 0, gdps: [] });
    c.n++; c.peso += f.ultimoPeso; if (f.gdp != null) c.gdps.push(f.gdp);
  });
  const catArr = Object.entries(cats).sort((a, b) => b[1].n - a[1].n);

  // Proyección de faena (objetivo 480 kg)
  const OBJ = 480;
  const proy = (gdpProm && pesoProm && gdpProm > 0 && pesoProm < OBJ) ? Math.ceil((OBJ - pesoProm) / (gdpProm * 30)) : null;

  if (pesadas.length === 0) return (
    <div style={{ textAlign: "center", padding: "44px 20px", color: C.muted, marginTop: 14 }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>⚖️</div>
      <div style={{ fontSize: 13 }}>Sin pesajes cargados. Registrá pesos desde la ficha de cada animal (Rodeo).</div>
      <div style={{ fontSize: 12, marginTop: 6 }}>Con 2+ pesajes por animal se calcula la ganancia diaria.</div>
    </div>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8, marginBottom: 16 }}>
        <StatCard val={gdpProm != null ? `${gdpProm.toFixed(2)} kg` : "—"} label="ganancia diaria prom." color={C.pasto} />
        <StatCard val={pesoProm != null ? `${Math.round(pesoProm)} kg` : "—"} label="peso promedio" />
        <StatCard val={conGdp.length} label="animales con GDP" />
        <StatCard val={proy != null ? `${proy} mes${proy !== 1 ? "es" : ""}` : "—"} label={`a ${OBJ}kg de faena`} color={C.tierra} />
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.arenaDark}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", padding: "10px 14px", borderBottom: `1px solid ${C.arenaDark}`, fontFamily: "monospace", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: C.mutedLight, fontWeight: 600 }}>
          <span>Categoría</span><span style={{ textAlign: "right" }}>Peso prom.</span><span style={{ textAlign: "right" }}>GDP kg/día</span>
        </div>
        {catArr.map(([cat, d]) => {
          const g = d.gdps.length ? d.gdps.reduce((s, x) => s + x, 0) / d.gdps.length : null;
          return (
            <div key={cat} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", padding: "10px 14px", borderBottom: `1px solid ${C.arena}`, fontSize: 13 }}>
              <span style={{ color: C.tierra, fontWeight: 600 }}>{cat} <span style={{ color: C.muted, fontWeight: 400 }}>· {d.n}</span></span>
              <span style={{ textAlign: "right", fontFamily: "monospace" }}>{Math.round(d.peso / d.n)} kg</span>
              <span style={{ textAlign: "right", fontFamily: "monospace", color: g != null ? (g >= 0 ? C.ok : C.danger) : C.mutedLight }}>{g != null ? g.toFixed(2) : "—"}</span>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10 }}>Proyección estimada con objetivo de faena {OBJ} kg y la ganancia diaria promedio actual.</div>
    </div>
  );
}
